import * as vp from 'vopay';
import { useEffect, useState, FunctionComponent } from 'react';
import { useLocation } from 'react-router-dom';
import { FormikErrors, FormikTouched } from 'formik';
import { CreateAutoBalanceRequest, CreateAutoBalanceHandler } from './types';
import { CreateAutoBalanceRows } from './buildCreateAutoBalanceTransferRows.helper';
import CreateAutoBalanceForm from './CreateAutoBlanceTransferForm';
import CreateAutoBalanceTransferReview from './CreateAutoBalanceTransferReview';
import AccountCreation from '../../AccountCreation/AccountCreationContainer';
import { PendingDataProvider } from '../../../../providers/PendingData.provider';
import { useClientData } from '../../../../providers/ClientData.provider';
import useFetchClientAccountList from '../../../../hooks/useApi/useFetchClientAccountList';
import { useTranslation } from 'react-i18next';

type TitleProps = {
  title: string;
};

const { Box, Title } = vp;

type FC = FunctionComponent;

const CreateAutoBalanceTransferContainer: FC = () => {
  const { state } = useLocation<TitleProps>();
  const [review, setReview] = useState(false);
  const [createAccountActive, setCreateAccountActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [currentStepSend] = useState(false);
  const [submitTransaction, setSubmitTransaction] = useState(false);
  const [formErrors, setFormErrors] = useState<FormikErrors<CreateAutoBalanceRequest>>({});
  const [formTouched, setFormTouched] = useState<FormikTouched<CreateAutoBalanceRequest>>({});
  const [accountRowsForm, setAccountRowsForm] = useState<CreateAutoBalanceRows[]>([]);
  const [submitSubForm, setSubmitForm] = useState<() => Promise<void>>(() => Promise.resolve());
  const [formSubmittedOnce, setFormSubmittedOnce] = useState(false);

  const {
    response: { ClientAccountsEnabled, Country, clientAccountsEnabled },
  } = useClientData();
  const [, , , fetchClientAccounts] = useFetchClientAccountList({});
  const [createAutoBalanceRequest, setCreateAutoBalanceRequest] = useState<CreateAutoBalanceRequest>({
    Currency: Country === 'US' ? 'USD' : 'CAD',
    Account: '',
    Balance: 0,
    StartDate: '',
    Frequency: 'Monthly',
    ClientAccountID: '',
    ClientAccountName: '',
  });
  const [shouldCreateAutoBalanceRequest, setShouldCreateAutoBalanceRequest] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AutoBalanceTransfer.Create' });

  // Fetch primary client account on mount if client accounts are enabled
  useEffect(() => {
    if (clientAccountsEnabled) {
      fetchClientAccounts({
        ClientType: '',
      })
        .then((res) => {
          if (!res.Success) throw new Error(res.ErrorMessage);

          const primaryClientAccount = Object.values(res.ClientAccounts || {}).find(
            (obj) => obj.IsPrimaryAccount === '1',
          );

          if (primaryClientAccount) {
            const displayName =
              primaryClientAccount.ClientName || primaryClientAccount.BusinessName || 'Primary Account';
            setCreateAutoBalanceRequest((prev) => ({
              ...prev,
              ClientAccountID: primaryClientAccount.ClientAccountID,
              ClientAccountName: displayName,
            }));
          }
        })
        .catch((err) => {
          console.error('Error fetching client accounts:', err);
        });
    }
  }, [clientAccountsEnabled]);

  const handlers: CreateAutoBalanceHandler = {
    onReview: (values) => {
      setCreateAutoBalanceRequest(values);
      setShouldCreateAutoBalanceRequest(true);
      setReview(true);
    },
    onAutoBalanceChange: (_values, errors, touched, submitForm, accountRows) => {
      setFormErrors(errors);
      setFormTouched(touched);
      setCreateAutoBalanceRequest(_values);
      if (_values.Account === 'new') {
        setCreateAccountActive(true);
      } else {
        setCreateAccountActive(false);
      }
      setAccountRowsForm(accountRows);
      setSubmitForm(() => submitForm);
      if (currentStep === 0 && Object.values(formErrors).length !== 0 && formSubmittedOnce) {
        void submitForm();
      }
    },

    onBack: () => {
      setReview(false);
      setShouldCreateAutoBalanceRequest(true);
    },
  };
  const onStepChange = (IdStep: number) => {
    setCurrentStep(IdStep);
  };
  const onStepReject = (IdStep: number) => {
    if (IdStep === 0) {
      void submitSubForm();
    }
  };
  const onMainFormSubmit = () => {
    if (Object.values(formErrors).length === 0) {
      setSubmitTransaction(true);
    } else {
      void submitSubForm();
      setFormSubmittedOnce(true);
    }
  };
  const onTeneativeSubmit = () => {
    if (Object.values(formErrors).length === 0 && createAccountActive === false) {
      setSubmitTransaction(true);
    } else {
      void submitSubForm();
      setFormSubmittedOnce(true);
    }
  };
  useEffect(() => {
    if (shouldCreateAutoBalanceRequest) {
      setCreateAutoBalanceRequest({
        Currency: createAutoBalanceRequest.Currency,
        Account: createAutoBalanceRequest.Account,
        Balance: createAutoBalanceRequest.Balance,
        StartDate: createAutoBalanceRequest.StartDate,
        Frequency: createAutoBalanceRequest.Frequency,
        ClientAccountID: createAutoBalanceRequest.ClientAccountID,
        ClientAccountName: createAutoBalanceRequest.ClientAccountName,
      });
    }
  }, [review]);
  useEffect(() => {
    if (submitTransaction) {
      setTimeout(() => {
        setSubmitTransaction(false);
      }, 500);
    }
  }, [submitTransaction]);

  return (
    <Box mb="md" jc="sb" flx="col" gap="lg">
      <Box mb="md" jc="sb">
        <Title title={state?.title === 'Update' ? t('updateTitle') : t('title')} />
      </Box>
      <PendingDataProvider>
        <AccountCreation
          extraSteps={[
            {
              label: t('Form.title'),
              content: <CreateAutoBalanceForm defaultValues={createAutoBalanceRequest} handlers={handlers} />,
              showStep: () => true,
              hasError: () => Object.values(formErrors).length !== 0 && formSubmittedOnce,
              percentageComplete: () =>
                getCompletionPercentage(createAutoBalanceRequest, formTouched, formErrors, true),
            },
          ]}
          customReview={
            <CreateAutoBalanceTransferReview
              defaultValues={createAutoBalanceRequest}
              submitTransaction={submitTransaction}
            />
          }
          isClientControlled
          isActive={createAccountActive}
          isAllowPaylink={false}
          onStepChange={onStepChange}
          onStepReject={onStepReject}
          stepUser={currentStep}
          stepUserChange={currentStepSend}
          showSave
          isAllowMove={Object.values(formErrors).length === 0 || createAccountActive}
          onComplete={onMainFormSubmit}
          onTeneativeSubmit={onTeneativeSubmit}
          defaultValues={{
            currency: 'CAD',
            paymentMethod: 'bank',
            ClientAccountsEnabled,
            clientAccount: '',
            account: createAutoBalanceRequest.Account,
            country: Country,
          }}
        />
      </PendingDataProvider>
    </Box>
  );
  function getCompletionPercentage(
    values: CreateAutoBalanceRequest,
    touched: FormikTouched<CreateAutoBalanceRequest>,
    errors: FormikErrors<CreateAutoBalanceRequest>,
    atLeastCurrentStep = true,
  ) {
    const fields = accountRowsForm.filter((x) => x.require);
    if (fields.length === 0) return atLeastCurrentStep ? 100 : 0;
    const filled = fields.filter((field) => (touched[field.id] || values[field.id]) && !errors[field.id]);
    return (filled.length / fields.length) * 100;
  }
};

export default CreateAutoBalanceTransferContainer;
